import React, { useState, useEffect, useRef } from "react";
import { Settings, Shield, Users, Lightbulb, Database, Cpu } from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Counter.css";

const Counter = () => {
  const [counts, setCounts] = useState({
    projects: 0,
    specialists: 0,
    clients: 0,
    solutions: 0,
  });
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const stats = [
    { id: "projects", target: 34, label: "Project Completed", icon: Settings },
    { id: "specialists", target: 25, label: "IT Specialist", icon: Users },
    { id: "clients", target: 70, label: "Happy Clients", icon: Shield },
    { id: "solutions", target: 108, label: "Smart Solution", icon: Lightbulb },
  ];

  const features = [
    {
      icon: Database,
      title: "Scalable to grow with your business",
    },
    {
      icon: Cpu,
      title: "Secure to protect your data and reputation",
    },
    {
      icon: Cpu,
      title: "Future-ready to adapt to tomorrow’s challenges",
    },
  ];

  // 🔹 AOS Initialize
  useEffect(() => {
    AOS.init({
      duration: 1200,
      once: true,
    });
  }, []);

  // 🔹 Intersection Observer for counter trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
      observer.disconnect();
    };
  }, [isVisible]);

  // 🔹 Counter animation
  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const intervalTime = duration / steps;
    let timer;

    const animateCount = () => {
      setCounts((prevCounts) => {
        const newCounts = { ...prevCounts };
        let allComplete = true;

        stats.forEach((stat) => {
          if (newCounts[stat.id] < stat.target) {
            const increment = Math.ceil(stat.target / steps);
            newCounts[stat.id] = Math.min(
              newCounts[stat.id] + increment,
              stat.target
            );
            allComplete = false;
          }
        });

        if (allComplete) clearInterval(timer);
        return newCounts;
      });
    };

    timer = setInterval(animateCount, intervalTime);
    return () => clearInterval(timer);
  }, [isVisible]);

  return (
    <div ref={sectionRef} className="min-h-screen bg-white relative" id="project-id">
      {/* Stats Section */}
      <div className="tp-counter-bg">
        <div className="relative py-20 flex items-center px-4 sm:px-6 lg:px-8 bg_color_counter overflow-hidden">
          <div className="relative containers">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={stat.id}
                    className="text-center text-white border-r last:border-r-0 md:border-r md:[&:nth-child(2)]:border-r-0 md:[&:nth-child(4)]:border-r-0 lg:last:border-r-0 lg:[&:nth-child(2)]:border-r"
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                  >
                    <div className="mb-4 flex justify-center">
                      <div className="w-16 h-16 border-2 border-white/30 rounded-lg flex items-center justify-center">
                        <Icon className="w-8 h-8" />
                      </div>
                    </div>
                    <div className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2">
                      {counts[stat.id].toLocaleString()}
                    </div>
                    <div className="text-white/80 text-sm md:text-base font-medium">
                      {stat.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="containers">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Image */}
            <div
              className="order-2 lg:order-1"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop"
                  alt="Team collaboration"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>

            {/* Right Content */}
            <div
              className="order-1 lg:order-2"
              data-aos="fade-left"
              data-aos-delay="300"
            >
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Tech Management
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Let’s Build Your Success with Dynamic Softlink <br />
                {/* <span className="text-blue-600">With Tecz Solution</span> */}
              </h2>

              <p className="text-gray-600 text-lg mb-10 leading-relaxed">
                We don’t just deliver software — we deliver business outcomes. Our collaborative approach ensures every solution is
              </p>

              {/* Features */}
              <div className="space-y-8">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={index}
                        className="flex items-center gap-4 group"
                      data-aos="fade-up"
                      data-aos-delay={400 + index * 100}
                    >
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-300">
                          <Icon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {feature.title}
                        </h3>

                      </div>
                    </div>
                  );
                })}
              </div>

              {/* CTA */}
              <div
                className="mt-10"
                data-aos="zoom-in"
                data-aos-delay="600"
              >
                <button className="button_same  text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                  Get Started Today
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/2 right-10 opacity-5">
        <div className="w-32 h-32 border border-gray-300 rounded-full"></div>
      </div>
      <div className="absolute bottom-20 left-10 opacity-5">
        <div className="w-24 h-24 border border-gray-300 rounded-full"></div>
      </div>
    </div>
  );
};

export default Counter;
